import { Box, HStack, Image, Link } from '@chakra-ui/react'
import { ContentNode } from '@helo/cms'
import { ReactNode } from 'react'

import expoNowLogo from '../../assets/expo-now-logo-white.png'
import { Icon } from '../../components/Icons/Icon'
import { Skeleton } from '../../components/SkeletonLoader'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { createWidget } from '../widget-utils'
import { ServiceProviderHeaderLayout } from './serviceProviderHeaderLayout'

type ServiceProviderConfig = {
  [key: string]: {
    getLinkContent: () => ReactNode
    getUrl: (shipmentId?: string) => string
  } | null
}

const serviceProviderConfig: ServiceProviderConfig = {
  Expeditors: {
    getUrl: (shipmentId?: string) =>
      `https://go2expo.expeditors.com/shipments/${shipmentId}/documents`,
    getLinkContent: () => (
      <Tooltip
        label={
          <Box textAlign="center">
            View EXP.O NOW
            <br /> Documents
          </Box>
        }
        placement="top"
        hasArrow
      >
        <HStack
          bg="#EB0028"
          p="4px"
          alignItems="center"
          gap="2px"
          display="inline-flex"
        >
          <Image h="10px" display="block" src={expoNowLogo} />

          <Icon icon="ExternalLinkIcon" boxSize="12px" color="#F7F7F7" />
        </HStack>
      </Tooltip>
    ),
  },
  'DHL (DMAL)': null,
  'Kintetsu World Express': null,
  icl: null,
  'top-ocean': null,
  'lm-brokers': null,
  'Seaboard Marine': null,
}

export type ServiceProviderLinkProps = {
  /**
   * ContentNode containing service provider and shipment information.
   * Expected to have fields:
   * - serviceProvider or shipmentServiceProvider: string (e.g., "Expeditors")
   * - shipmentId or shipmentDocumentId: string
   */
  contentNode?: ContentNode
  isLoading?: boolean
}

export const ServiceProviderLinkWidget = ({
  contentNode,
  isLoading,
}: ServiceProviderLinkProps) => {
  if (isLoading) {
    const { width, height, rounded } = ServiceProviderHeaderLayout.providerLink

    return (
      <Skeleton
        isLoading
        w={width}
        h={height}
        rounded={rounded}
        flexShrink={0}
      />
    )
  }

  if (!contentNode) return null

  // Extract scalar value from content node (handles nested structures)
  const extractValue = (node: any): any => {
    if (!node) return null
    if (node.kind === 'SCALAR' || node.kind === 'ENUM') return node.data
    if (node.kind === 'LIST' && node.items?.length > 0)
      return extractValue(node.items[0])
    if (node.kind === 'FIELD' && node.ofType) return extractValue(node.ofType)
    if (
      typeof node.data === 'string' ||
      typeof node.data === 'number' ||
      typeof node.data === 'boolean'
    )
      return node.data
    return node.data
  }

  // Get target node (handle LIST)
  let targetNode = contentNode
  if (contentNode.kind === 'LIST' && contentNode.items?.length > 0) {
    targetNode = contentNode.items[0]
  }
  if (targetNode.kind !== 'OBJECT' || !targetNode.fields) return null

  // Extract data from fields
  const data: Record<string, string> = {}
  for (const field of targetNode.fields) {
    const value = extractValue(field)
    if (value != null) data[field.key] = String(value)
  }

  const serviceProvider = data.serviceProvider || data.shipmentServiceProvider
  const shipmentId = data.shipmentId || data.shipmentDocumentId

  if (!serviceProvider || !shipmentId) return null

  const config = serviceProviderConfig[serviceProvider]
  if (!config) return null

  return (
    <Link
      href={config.getUrl(shipmentId)}
      isExternal
      display="inline-flex"
      alignItems="center"
      _hover={{ textDecoration: 'none' }}
    >
      {config.getLinkContent()}
    </Link>
  )
}

export const ServiceProviderLinkWidgetDefinition = createWidget(
  ServiceProviderLinkWidget,
  {
    type: 'ServiceProviderLink',
  },
)
